import React, { useState, useRef, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AUTH_ENDPOINTS } from "../../constants/endpoints";
import { AuthContext } from "../../context/Authcontext";
import { BsJustify } from "react-icons/bs";
import { FaUserCircle, FaSignOutAlt } from "react-icons/fa";
import { useRegistrationStatus } from "../../hooks/useRegistrationStatus";
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import screen1 from "/images/screen1.png";
import screen2 from "/images/screen2.png";
import screen3 from "/images/screen3.png";
import apiClient from "../../services/apiClient";

const Header = ({ OpenSidebar }) => {
  const tourSteps = [
    {
      src: screen1,
      intro: "Start by entering your personal details here.",
    },
    {
      src: screen2,
      intro: "Fill out your academic information in this section.",
    },
    {
      src: screen3,
      intro: "Review and submit your application here.",
    },
  ];

  const { isRegistrationAllowed, isLoading } = useRegistrationStatus(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showImageTour, setShowImageTour] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    fullName,
    profileImage,
    emailInfo,
    logout,
    lastLogin,
    token,
    newStudent,
  } = useContext(AuthContext);

  const formattedDateTime = new Date(lastLogin).toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const [buttonLabel, setButtonLabel] = useState("Apply Now");

  useEffect(() => {
    const checkSchemeData = async () => {
      if (!newStudent) {
        try {
          const response = await apiClient.get(AUTH_ENDPOINTS.CHECK_ELIGIBILITY);

          const { status, message, data } = response.data;

          if (status && message === "You are eligible") {
            const hasSchemes =
              Array.isArray(data?.scheme_data) && data.scheme_data.length > 0;
            setButtonLabel(hasSchemes ? "Update" : "Apply Now");
          } else {
            setButtonLabel("Update");
          }
        } catch (error) {
          setButtonLabel("Update");
        }
      } else {
        setButtonLabel("Apply Now");
      }
    };

    checkSchemeData();
  }, [newStudent, token]);

  const startTour = () => {
    const tour = driver({
      showProgress: true,
      animate: true,
      doneBtnText: "Done",
      closeBtnText: "Close",
      nextBtnText: "Next",
      prevBtnText: "Back",
      steps: [
        {
          element: ".tour-apply-button",
          popover: {
            title: "Apply or Update",
            description: "Click here to start or update your application.",
            position: "left",
          },
        },
      ],
    });

    tour.drive();
  };


  const handleApplyNow = async () => {
    try {
      const response = await apiClient.get(AUTH_ENDPOINTS.CHECK_ELIGIBILITY);
      const { status, message } = response.data;

      if (!(status && message === "You are eligible")) {
        toast.error(message || "You are not eligible to apply.");
        return;
      }
    } catch (error) {
      toast.error("Eligibility check failed.");
      return;
    }

    // Start single-step tour
    const intro = introJs();

    intro.setOptions({
      steps: [
        {
          element: '.tour-apply-button',
          intro: 'Click here to apply or update your application.',
          position: 'left'
        }
      ],
      nextLabel: 'Go to Form',
      showStepNumbers: false,
      exitOnOverlayClick: false,
      exitOnEsc: false,
      hidePrev: true,
      hideNext: false,
      doneLabel: 'Proceed',
    });

    intro.oncomplete(() => {
      navigate("/dashboard/apply", { state: { startTour: true } });
    });

    intro.onexit(() => {
      // If user closes without completing, still allow navigation
      navigate("/dashboard/apply");
    });

    intro.start();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isFormPage = location.pathname === "/dashboard/apply";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="header d-flex justify-content-between align-items-center bg-light">
      <div className="menu-icon" onClick={OpenSidebar}>
        <BsJustify className="icon fs-3" />
      </div>

      <div className="header-left">
        <span>Welcome back 👋</span>
        <h6>Hello {fullName}</h6>
        <small>Last Login From: {formattedDateTime}</small>
      </div>

      {/* when registeration is close then not show the apply button  */}
      <div className="header-right position-relative" ref={dropdownRef}>
        {!isFormPage && (
          <button
            className="apply btn btn-success mx-3 tour-apply-button"
            onClick={handleApplyNow}
            disabled={!isRegistrationAllowed && !isLoading}
            style={{
              opacity: !isRegistrationAllowed ? 0.6 : 1,
              cursor: !isRegistrationAllowed ? "not-allowed" : "pointer",
            }}
          >
            {buttonLabel}
          </button>
        )}


        <img
          src={profileImage || "/images/dummy.png"}
          alt="Profile"
          className="rounded-circle"
          style={{ width: 40, height: 40, cursor: "pointer" }}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        />

        {dropdownOpen && (
          <div
            className="position-absolute bg-white shadow rounded"
            style={{
              top: "60px",
              right: 0,
              width: "375px",
              zIndex: 999,
            }}
          >
            <div className="bg-success text-white p-4 rounded-top">
              <div className="d-flex gap-3">
                <img
                  src={profileImage || "/images/dummy.png"}
                  alt="Profile"
                  className="rounded-circle"
                  style={{ width: 40, height: 40 }}
                />
                <div>
                  <h6 className="mb-0 profile_name">{fullName}</h6>
                  <small className="profile_email">{emailInfo}</small>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="d-flex align-items-center mb-2">
                <FaUserCircle className="me-2" size={24} />
                <div
                  onClick={() => {
                    if (window.innerWidth <= 768) {
                      setTimeout(() => {
                        OpenSidebar(false);
                      }, 100);
                    }
                    navigate("/dashboard/profile");
                    setDropdownOpen(false);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <p className="mb-0 profile_my">My Profile</p>
                  <small className="drop_subcaption">
                    Account settings and more
                  </small>
                </div>
              </div>

              <div
                className="d-flex align-items-center text-danger"
                style={{ cursor: "pointer" }}
                onClick={handleLogout}
              >
                <FaSignOutAlt className="me-2" size={24} />
                <div>
                  <p className="mb-0 logout">Logout</p>
                  <small className="drop_subcaption">
                    Logout to home page
                  </small>
                </div>
              </div>
            </div>

          </div>

        )}
        {showImageTour && (
          <div className="tour-overlay">
            <div className="tour-image-wrapper">

              <div
                id="tour-step-0"
                className={`tour-step-image-container ${currentImageIndex === 0 ? 'show' : 'hide'} top-right`}
              >
                <img
                  src={screen1}
                  alt="Tour Step 1"
                  className="form-tour-image"
                />
              </div>

              <div
                id="tour-step-1"
                className={`tour-step-image-container ${currentImageIndex === 1 ? 'show' : 'hide'} center-left`}
              >
                <img
                  src={screen2}
                  alt="Tour Step 2"
                  className="form-tour-image"
                />
              </div>

              <div
                id="tour-step-2"
                className={`tour-step-image-container ${currentImageIndex === 2 ? 'show' : 'hide'} bottom-right`}
              >
                <img
                  src={screen3}
                  alt="Tour Step 3"
                  className="form-tour-image"
                />
              </div>

            </div>
          </div>
        )}




      </div>
    </header>
  );
};

export default Header;
